import React, { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Plus, AlertCircle } from 'lucide-react';
import { AppContext } from '../App';

/**
 * HSN-first commodity picker for PO line items.
 *
 * UX (matches user-approved flow):
 *   1. User types an HSN/SAC code.
 *   2. On blur / debounce the picker fetches master commodities matching
 *      that code.
 *   3. If 0 matches  → tiny "no master entry" hint with an inline link to
 *                     add it to master (caller handles via onAddToMaster).
 *      If 1 match   → auto-selected (commodity name + GST auto-fill).
 *      If N matches → user picks from a dropdown of matching names.
 *
 * Props:
 *   - hsnCode      : current HSN/SAC string on this row
 *   - commodityName: current commodity name string on this row
 *   - gstRate      : current GST rate (used as fallback display)
 *   - kind         : 'commodity' | 'service' (drives endpoint + labels)
 *   - onChange     : ({ hsnCode, commodityName, gstRate, commodityId,
 *                       commodityCode, uom }) => void  (partial patch)
 *   - onAddToMaster: () => void  (optional — caller opens an add-master modal)
 *   - disabled     : boolean
 *   - testIdPrefix : string (for data-testid attributes)
 *
 * Notes:
 *   - Caller stores commodityId + commodityCode on the row so editing an
 *     existing PO renders the previously-selected commodity correctly.
 *   - The picker NEVER auto-overwrites the commodity name if the user is
 *     editing a saved PO whose name is not in master — it surfaces a small
 *     "not in master" warning instead.
 */
export default function HsnCommodityPicker({
  hsnCode = '',
  commodityName = '',
  gstRate = '',
  commodityId = '',
  kind = 'commodity',
  onChange = () => {},
  onAddToMaster,
  disabled = false,
  testIdPrefix = 'hsn-picker',
}) {
  const { API } = useContext(AppContext);
  const [localHsn, setLocalHsn] = useState(hsnCode || '');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(Boolean(hsnCode));
  const lastFetchedHsn = useRef('');

  // Keep localHsn in sync when parent updates (e.g. row reset)
  useEffect(() => { setLocalHsn(hsnCode || ''); }, [hsnCode]);

  const codeLabel = kind === 'service' ? 'SAC' : 'HSN';
  const endpoint = kind === 'service' ? '/stores/master/services-by-sac' : '/stores/master/commodities-by-hsn';
  const queryKey = kind === 'service' ? 'sac' : 'hsn';
  const namedField = kind === 'service' ? 'sacDescription' : 'commodityName';
  const codeField = kind === 'service' ? 'sacCode' : 'hsnCode';

  // Fetch commodities matching the current HSN
  const fetchByCode = async (code) => {
    if (!code || !code.trim()) {
      setOptions([]);
      return [];
    }
    if (lastFetchedHsn.current === code) return options;
    setLoading(true);
    try {
      const r = await axios.get(`${API}${endpoint}`, { params: { [queryKey]: code.trim() } });
      const list = r.data?.data || [];
      lastFetchedHsn.current = code;
      setOptions(list);
      return list;
    } catch {
      setOptions([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Run a lookup whenever hsnCode changes from outside (e.g., row reset)
  useEffect(() => {
    if (hsnCode && hsnCode !== lastFetchedHsn.current) {
      fetchByCode(hsnCode);
    } else if (!hsnCode) {
      setOptions([]);
      lastFetchedHsn.current = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hsnCode]);

  const applyCommodity = (c) => {
    if (!c) return;
    onChange({
      hsnCode: c[codeField] || localHsn,
      commodityName: c[namedField] || '',
      gstRate: c.gstRate != null ? c.gstRate : gstRate,
      commodityId: c.id || '',
      commodityCode: c.commodityCode || (kind === 'service' ? (c.sacCode || '') : ''),
      uom: c.uom || '',
    });
  };

  const handleHsnBlur = async () => {
    setTouched(true);
    if (localHsn === hsnCode && options.length > 0) return;
    // Push the new HSN to parent immediately (so the row state has it)
    if (localHsn !== hsnCode) {
      onChange({ hsnCode: localHsn });
    }
    const list = await fetchByCode(localHsn);
    if (list.length === 1) {
      // Single match → auto-select
      applyCommodity(list[0]);
    } else if (list.length === 0 && localHsn.trim()) {
      // 0 matches → clear the commodity name so user knows nothing was picked
      if (commodityName) {
        // Don't blow away the existing name silently; just warn via UI.
      }
    }
  };

  const handleSelectFromDropdown = (e) => {
    const selectedId = e.target.value;
    const picked = options.find(o => o.id === selectedId);
    applyCommodity(picked);
  };

  // Does the current commodityName match any option's name? (used to surface
  // "name is no longer in master" warnings on edit-existing-PO).
  const nameInMaster = commodityName
    ? options.some(o => (o[namedField] || '').toLowerCase() === commodityName.toLowerCase())
    : false;

  return (
    <div className="space-y-1">
      {/* HSN/SAC entry */}
      <div className="flex items-center gap-2">
        <label className="text-[10px] uppercase font-medium text-gray-500 w-10 flex-shrink-0">
          {codeLabel}
        </label>
        <input
          type="text"
          value={localHsn}
          onChange={(e) => setLocalHsn(e.target.value)}
          onBlur={handleHsnBlur}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleHsnBlur(); } }}
          placeholder={`Enter ${codeLabel} code`}
          disabled={disabled}
          className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm focus:border-amber-400 outline-none disabled:bg-gray-50"
          data-testid={`${testIdPrefix}-${codeLabel.toLowerCase()}-input`}
        />
        {loading && <span className="text-[10px] text-gray-400">…</span>}
      </div>

      {/* Commodity name — dropdown OR auto-selected display */}
      {touched && localHsn && (
        <>
          {options.length === 0 && !loading && (
            <div
              className="flex items-start gap-1.5 px-2 py-1.5 bg-rose-50 border border-rose-200 rounded text-[11px] text-rose-800"
              data-testid={`${testIdPrefix}-no-master`}
            >
              <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div>No commodity in master with {codeLabel} <code className="bg-white px-1 rounded">{localHsn}</code>.</div>
                {onAddToMaster && (
                  <button
                    type="button"
                    onClick={onAddToMaster}
                    className="mt-0.5 underline font-medium hover:text-rose-900 flex items-center gap-0.5"
                    data-testid={`${testIdPrefix}-add-to-master`}
                  >
                    <Plus size={10} /> Add to master
                  </button>
                )}
              </div>
            </div>
          )}

          {options.length >= 1 && (
            <select
              value={commodityId || ''}
              onChange={handleSelectFromDropdown}
              disabled={disabled}
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:border-amber-400 outline-none disabled:bg-gray-50"
              data-testid={`${testIdPrefix}-commodity-select`}
            >
              <option value="">
                {options.length === 1
                  ? `Pick: ${options[0][namedField] || '(unnamed)'}`
                  : `Select commodity (${options.length} options)`}
              </option>
              {options.map((o) => (
                <option key={o.id} value={o.id}>
                  {o[namedField] || '(unnamed)'}
                  {o.gstRate != null ? ` — ${o.gstRate}% GST` : ''}
                </option>
              ))}
            </select>
          )}

          {/* Edit-existing-PO safeguard: name was set but no longer in master */}
          {commodityName && options.length > 0 && !nameInMaster && (
            <div
              className="text-[11px] text-amber-700 px-1"
              data-testid={`${testIdPrefix}-name-not-in-master`}
            >
              ⚠ Current name <em>“{commodityName}”</em> is not in the master for this {codeLabel}. Pick one from the dropdown above.
            </div>
          )}
        </>
      )}

      {/* Current selection summary (after pick) */}
      {commodityName && (
        <div
          className="text-[11px] text-gray-700 truncate font-medium px-1"
          title={commodityName}
          data-testid={`${testIdPrefix}-current-name`}
        >
          {commodityName}
        </div>
      )}
    </div>
  );
}
